// Chat types - AI Financial Advisor chat interface

// ============================================================================
// MESSAGE TYPES
// ============================================================================

export type MessageRole = 'user' | 'assistant' | 'system'

export type MessageStatus = 'sending' | 'sent' | 'error'

export interface ChatMessage {
  id: string
  role: MessageRole
  content: string
  timestamp: string
  status: MessageStatus
  
  // For assistant messages
  isStreaming?: boolean
  
  // For error messages
  error?: string
  
  // Metadata
  tokenCount?: number
  responseTime?: number          // ms
}

// ============================================================================
// SUGGESTED PROMPTS
// ============================================================================

export type PromptCategory = 
  | 'retirement'
  | 'goals'
  | 'debt'
  | 'investing'
  | 'insurance'
  | 'taxes'
  | 'family'
  | 'career'
  | 'general'

export interface SuggestedPrompt {
  id: string
  category: PromptCategory
  prompt: string
  icon: string
  description?: string
  
  // Condition to show this prompt
  showIf?: {
    hasChildren?: boolean
    hasDebt?: boolean
    nearRetirement?: boolean     // Within 10 years
    hasGoals?: boolean
    lowSavingsRate?: boolean     // < 10%
    highDebtRatio?: boolean      // DTI > 40%
  }
}

// ============================================================================
// CHAT CONTEXT (Injected into every message)
// ============================================================================

export interface ChatFinancialContext {
  // Personal snapshot
  personal: {
    age: number
    retirementAge: number
    yearsToRetirement: number
    lifeExpectancy: number
    location: string
    filingStatus: string
  }
  
  // Family situation
  family: {
    maritalStatus: string
    hasSpouse: boolean
    spouseAge?: number
    spouseIncome?: number
    numberOfChildren: number
    childrenAges: number[]
    plannedChildren: number
    hasDependents: boolean
  }
  
  // Career
  career: {
    employmentStatus: string
    industry: string
    jobTitle: string
    yearsExperience: number
    currentSalary: number
    totalCompensation: number
    hasSideIncome: boolean
    sideIncomeTotal: number
  }
  
  // Current finances
  finances: {
    netWorth: number
    totalAssets: number
    totalDebts: number
    liquidAssets: number
    retirementAssets: number
    monthlyIncome: number
    monthlyExpenses: number
    monthlySavings: number
    savingsRate: number
    emergencyFundMonths: number
  }
  
  // Assets breakdown
  assets: {
    cash: number
    investments: number
    retirement401k: number
    retirementIRA: number
    realEstateEquity: number
    otherAssets: number
  }
  
  // Debts breakdown
  debts: {
    mortgage: number
    studentLoans: number
    creditCards: number
    autoLoans: number
    otherDebts: number
    totalMonthlyPayments: number
    highestInterestRate: number
    debtToIncomeRatio: number
  }
  
  // Insurance coverage
  insurance: {
    hasHealthInsurance: boolean
    healthInsuranceType: string
    hasLifeInsurance: boolean
    lifeInsuranceCoverage: number
    hasDisabilityInsurance: boolean
    disabilityCoverage: number
  }
  
  // Goals summary
  goals: {
    totalGoals: number
    topGoals: {
      name: string
      targetAmount: number
      currentProgress: number
      targetDate: string
      yearsAway: number
    }[]
  }
  
  // Timeline summary
  timeline: {
    upcomingEvents: {
      name: string
      type: string
      date: string
      financialImpact: number
    }[]
    currentPhase: string
    nextMilestone?: string
  }
  
  // Projections
  projections: {
    wealthAtRetirement: number
    retirementReadinessScore: number  // 0-100
    socialSecurityEstimate: number
    projectedRetirementIncome: number
    yearsOfRetirementFunded: number
  }
  
  // Risk indicators
  risks: {
    hasEmergencyFund: boolean
    isUnderinsured: boolean
    highDebtBurden: boolean
    onTrackForRetirement: boolean
    biggestRisk: string
  }
}

// ============================================================================
// CHAT STATE
// ============================================================================

export interface ChatState {
  isOpen: boolean
  isMinimized: boolean
  messages: ChatMessage[]
  isLoading: boolean
  error: string | null
  
  // Session
  sessionId: string
  startedAt: string
  
  // Settings
  suggestedPromptsVisible: boolean
}

// ============================================================================
// INITIAL STATE
// ============================================================================

export const createInitialChatState = (): ChatState => ({
  isOpen: false,
  isMinimized: false,
  messages: [],
  isLoading: false,
  error: null,
  sessionId: '',
  startedAt: '',
  suggestedPromptsVisible: true,
})

// ============================================================================
// SUGGESTED PROMPTS DATA
// ============================================================================

export const SUGGESTED_PROMPTS: SuggestedPrompt[] = [
  // Retirement
  {
    id: 'retirement-track',
    category: 'retirement',
    prompt: 'Am I on track for retirement?',
    icon: 'Target',
    description: 'Get a detailed analysis of your retirement readiness',
  },
  {
    id: 'retirement-early',
    category: 'retirement',
    prompt: 'What if I want to retire 5 years earlier?',
    icon: 'Calendar',
    description: 'See how early retirement affects your plan',
  },
  {
    id: 'retirement-income',
    category: 'retirement',
    prompt: 'How much income will I have in retirement?',
    icon: 'DollarSign',
  },
  
  // Goals
  {
    id: 'goals-priority',
    category: 'goals',
    prompt: 'Which of my goals should I prioritize?',
    icon: 'ListOrdered',
  },
  {
    id: 'goals-house',
    category: 'goals',
    prompt: 'How much house can I afford?',
    icon: 'Home',
  },
  
  // Debt
  {
    id: 'debt-strategy',
    category: 'debt',
    prompt: 'What\'s the best strategy to pay off my debt?',
    icon: 'CreditCard',
    showIf: { hasDebt: true },
  },
  {
    id: 'debt-vs-invest',
    category: 'debt',
    prompt: 'Should I pay off debt or invest more?',
    icon: 'Scale',
    showIf: { hasDebt: true },
  },
  
  // Investing
  {
    id: 'invest-allocation',
    category: 'investing',
    prompt: 'Is my investment allocation appropriate for my age?',
    icon: 'PieChart',
  },
  {
    id: 'invest-more',
    category: 'investing',
    prompt: 'How much more should I be saving each month?',
    icon: 'TrendingUp',
    showIf: { lowSavingsRate: true },
  },
  
  // Insurance
  {
    id: 'insurance-life',
    category: 'insurance',
    prompt: 'Do I have enough life insurance?',
    icon: 'Shield',
  },
  {
    id: 'insurance-disability',
    category: 'insurance',
    prompt: 'Should I get disability insurance?',
    icon: 'Umbrella',
  },
  
  // Family
  {
    id: 'family-kid',
    category: 'family',
    prompt: 'What if I have another child in 2 years?',
    icon: 'Baby',
  },
  {
    id: 'family-college',
    category: 'family',
    prompt: 'How should I save for my kids\' college?',
    icon: 'GraduationCap',
    showIf: { hasChildren: true },
  },
  
  // Career
  {
    id: 'career-outlook',
    category: 'career',
    prompt: 'How does my industry outlook affect my plan?',
    icon: 'Briefcase',
  },
  {
    id: 'career-change',
    category: 'career',
    prompt: 'What if I switch to a lower-paying but more fulfilling job?',
    icon: 'ArrowRightLeft',
  },
  
  // General
  {
    id: 'general-risk',
    category: 'general',
    prompt: 'What\'s my biggest financial risk right now?',
    icon: 'AlertTriangle',
  },
  {
    id: 'general-improve',
    category: 'general',
    prompt: 'What\'s the #1 thing I should do to improve my finances?',
    icon: 'Lightbulb',
  },
  {
    id: 'general-emergency',
    category: 'general',
    prompt: 'How many months of expenses do I have saved?',
    icon: 'Vault',
  },
]

// ============================================================================
// SYSTEM PROMPT TEMPLATE
// ============================================================================

export const AI_SYSTEM_PROMPT = `You are a knowledgeable and empathetic personal financial advisor with complete access to the user's financial situation. Your role is to provide specific, actionable advice based on their actual numbers.

## Your Capabilities:
- You know their complete financial picture: income, expenses, assets, debts, insurance, goals, and life timeline
- You can calculate projections and scenarios based on their data
- You understand their industry and can factor in career trajectory
- You account for their family situation (spouse, children, planned children)
- All amounts you discuss are in TODAY'S DOLLARS (real, inflation-adjusted) unless specifically asked otherwise

## Guidelines:
1. Be specific - reference their actual numbers, not generic advice
2. Be direct but supportive - give honest assessments
3. When discussing future values, always clarify if it's in today's dollars
4. If they ask about something not in their plan, offer to help them add it
5. Acknowledge uncertainty where appropriate (market returns, life events)
6. Prioritize their stated goals while noting any concerns
7. Keep responses concise but thorough - use bullet points for clarity
8. If they seem stressed about finances, be reassuring while still being realistic

## Context Interpretation:
- "savingsRate" below 10% is low, 10-20% is good, 20%+ is excellent
- "retirementReadinessScore" of 80+ means on track, 60-79 needs attention, below 60 needs significant changes
- "emergencyFundMonths" should ideally be 3-6 months
- "debtToIncomeRatio" above 36% is concerning, above 43% is problematic

## Current User Profile:
{CONTEXT_JSON}

The user's question is:`

// ============================================================================
// HELPERS
// ============================================================================

export function generateMessageId(): string {
  return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

export function generateSessionId(): string {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}
